import { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { message, notification } from "antd";
import { ClockCircleOutlined, LogoutOutlined } from "@ant-design/icons";
import { logout } from "../actions/auth";

/**
 * ============================================================
 * HOOK : useIdleTimer
 * ============================================================
 *
 * Détecte l'inactivité utilisateur et déconnecte automatiquement
 * après 30 minutes sans action.
 *
 * Fonctionnement :
 *   - À 28 min d'inactivité → Notification toast (warning)
 *   - À 30 min d'inactivité → Déconnexion automatique + redirection /login
 *   - Toute activité (souris, clavier, scroll, touch) reset le timer
 *
 * Configuration via constantes en haut du fichier.
 * ============================================================
 */

// ============================================================
// ⚙️ CONFIGURATION
// ============================================================
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;   // 30 minutes
const WARNING_BEFORE_MS = 2 * 60 * 1000;  // Avertissement 2 min avant


// const IDLE_TIMEOUT_MS = 30 * 1000;   // 🧪 TEST : 30 secondes
// const WARNING_BEFORE_MS = 15 * 1000; // 🧪 TEST : warning à 15 sec

const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
  "wheel",
];

// 🎨 Couleurs Nomidya
const NOMIDYA_BLUE = "#0054A6";
const NOMIDYA_ORANGE = "#E8860C";

const useIdleTimer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoggedIn } = useSelector((state) => state.auth);

  // Refs pour éviter les re-renders inutiles
  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const warningShownRef = useRef(false);
  const lastActivityRef = useRef(Date.now());

  // ============================================================
  // 🚪 Déconnexion automatique
  // ============================================================
  const handleAutoLogout = useCallback(() => {
    // Fermer toute notification d'avertissement en cours
    notification.destroy("idle-warning");

    // Notification de déconnexion
    notification.warning({
      key: "idle-logout",
      message: "Session expirée",
      description:
        "Vous avez été déconnecté après 30 minutes d'inactivité pour des raisons de sécurité.",
      icon: <LogoutOutlined style={{ color: NOMIDYA_ORANGE }} />,
      duration: 8,
      placement: "topRight",
    });

    // Logout via Redux
    dispatch(logout());

    // Nettoyage localStorage (sécurité supplémentaire)
    try {
      localStorage.removeItem("user");
    } catch (e) {
      // ignore
    }

    // Redirection vers login
    setTimeout(() => {
      navigate("/login");
      window.location.reload();
    }, 500);
  }, [dispatch, navigate]);

  // ============================================================
  // ⚠️ Avertissement avant déconnexion (toast)
  // ============================================================
  const showWarning = useCallback(() => {
    if (warningShownRef.current) return;
    warningShownRef.current = true;

    notification.open({
      key: "idle-warning",
      message: "⚠️ Inactivité détectée",
      description:
        "Vous serez déconnecté dans 2 minutes pour cause d'inactivité. Bougez la souris ou appuyez sur une touche pour rester connecté.",
      icon: <ClockCircleOutlined style={{ color: NOMIDYA_ORANGE }} />,
      duration: 0, // ne se ferme pas tout seul
      placement: "topRight",
      style: {
        borderLeft: `4px solid ${NOMIDYA_ORANGE}`,
        borderRadius: 8,
      },
    });
  }, []);

  // ============================================================
  // 🔄 Reset des timers à chaque activité
  // ============================================================
  const resetTimers = useCallback(() => {
    // Throttle : ne reset que si dernière activité > 1s
    // (évite de spammer pendant un mousemove continu)
    const now = Date.now();
    if (now - lastActivityRef.current < 1000) {
      return;
    }
    lastActivityRef.current = now;

    // Si on avait montré le warning, le fermer (user de retour)
    if (warningShownRef.current) {
      notification.destroy("idle-warning");
      warningShownRef.current = false;
      message.success({
        content: "✅ Session prolongée",
        duration: 2,
        style: { marginTop: 20 },
      });
    }

    // Reset les timers existants
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);

    // Redémarre les timers
    warningTimerRef.current = setTimeout(
      showWarning,
      IDLE_TIMEOUT_MS - WARNING_BEFORE_MS
    );
    idleTimerRef.current = setTimeout(handleAutoLogout, IDLE_TIMEOUT_MS);
  }, [handleAutoLogout, showWarning]);

  // ============================================================
  // 🎬 Mise en place / nettoyage des listeners
  // ============================================================
  useEffect(() => {
    // Si l'utilisateur n'est pas connecté, on ne fait rien
    if (!isLoggedIn) {
      // Cleanup au cas où
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      notification.destroy("idle-warning");
      warningShownRef.current = false;
      return;
    }

    // Initialiser les timers au montage
    resetTimers();

    // Ajouter les listeners d'activité
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, resetTimers, { passive: true });
    });

    // ============================================================
    // 🔄 Cas spécial : multi-onglets
    //    Si un autre onglet a une activité, on récupère via storage
    // ============================================================
    const handleStorageActivity = (e) => {
      if (e.key === "nomidya_last_activity") {
        resetTimers();
      }
    };
    window.addEventListener("storage", handleStorageActivity);

    // Propage l'activité aux autres onglets
    const propagateActivity = () => {
      try {
        localStorage.setItem("nomidya_last_activity", Date.now().toString());
      } catch (e) {
        // ignore
      }
    };
    const propagateInterval = setInterval(propagateActivity, 30000); // toutes les 30s

    // Cleanup au démontage
    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, resetTimers);
      });
      window.removeEventListener("storage", handleStorageActivity);
      clearInterval(propagateInterval);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      notification.destroy("idle-warning");
    };
  }, [isLoggedIn, resetTimers]);
};

export default useIdleTimer;